import {
  Box2d,
  SelectionHandle,
  StateNode,
  T,
  TLBaseShape,
  TLEnterEventHandler,
  TLEventHandlers,
  TLPointerEventInfo,
  TLShapePartial,
  Vec2d,
  deepCopy,
  rotateSelectionHandle,
  vec2dModelValidator,
} from '@tldraw/tldraw'
import { MaskShape } from 'components/VisualStudioPlayground/Shapes/MaskShape/Types'
import { getContentBounds } from 'components/VisualStudioPlayground/Shapes/MaskShape/helpers'
import { setMaskingShapeId } from 'components/VisualStudioPlayground/VisualEditor/helpers'

export const MIN_MASK_SIZE = 8

export const MaskShapeMask = T.object({
  topLeft: vec2dModelValidator,
  bottomRight: vec2dModelValidator,
})

export type ShapeMask = T.TypeOf<typeof MaskShapeMask>
type Snapshot = ReturnType<Masking['createSnapshot']>

export class Masking extends StateNode {
  static override id = 'masking'

  info = {} as TLPointerEventInfo & {
    target: 'selection'
    handle: SelectionHandle
    onInteractionEnd?: string
  }

  markId = ''

  private snapshot = {} as any as Snapshot

  override onEnter: TLEnterEventHandler = (
    info: TLPointerEventInfo & {
      target: 'selection'
      handle: SelectionHandle
      onInteractionEnd?: string
    },
  ) => {
    this.info = info
    this.markId = 'masking'
    this.editor.mark(this.markId)
    this.snapshot = this.createSnapshot()
    this.updateShapes()
  }

  override onPointerMove: TLEventHandlers['onPointerMove'] = () => {
    this.updateShapes()
  }

  override onPointerUp: TLEventHandlers['onPointerUp'] = () => {
    this.complete()
  }

  override onComplete: TLEventHandlers['onComplete'] = () => {
    this.complete()
  }

  override onCancel: TLEventHandlers['onCancel'] = () => {
    this.cancel()
  }

  private getDefaultMask = (): ShapeMask => ({
    topLeft: { x: 0, y: 0 },
    bottomRight: { x: 1, y: 1 },
  })

  private updateShapes() {
    const { shape, cursorHandleOffset } = this.snapshot

    if (!shape) return

    const parent = this.editor.getShapeParent(shape)
    const parentPosition = parent ? new Vec2d(parent.x, parent.y) : new Vec2d(0, 0)

    const util = this.editor.getShapeUtil<MaskShape>('mask')
    if (!util) return

    const props = shape.props
    const currentPagePoint = this.editor.inputs.currentPagePoint.clone().sub(cursorHandleOffset)
    const originPagePoint = this.editor.inputs.originPagePoint.clone().sub(cursorHandleOffset)
    const mask = props.mask ?? this.getDefaultMask()
    const newMask = deepCopy(mask)

    // original (unmasked) width and height of shape
    const w = (1 / (mask.bottomRight.x - mask.topLeft.x)) * props.w
    const h = (1 / (mask.bottomRight.y - mask.topLeft.y)) * props.h

    let maskWithPixelCoordinates = {
      topLeft: {
        x: newMask.topLeft.x * w,
        y: newMask.topLeft.y * h,
      },
      bottomRight: {
        x: newMask.bottomRight.x * w,
        y: newMask.bottomRight.y * h,
      },
    }

    let newW = w
    let newH = h

    const contentBoundingBox = Box2d.From(getContentBounds(shape)).translate(parentPosition)
    const rotation = shape.rotation
    const scaleOriginHandle = rotateSelectionHandle(this.info.handle, Math.PI)
    const scaleOriginPage = Vec2d.RotWith(
      contentBoundingBox.getHandlePoint(scaleOriginHandle),
      contentBoundingBox.point,
      rotation,
    )

    const distanceFromScaleOriginNow = Vec2d.Sub(currentPagePoint, scaleOriginPage).rot(-rotation)
    const distanceFromScaleOriginAtStart = Vec2d.Sub(originPagePoint, scaleOriginPage).rot(-rotation)

    let scale = Vec2d.DivV(distanceFromScaleOriginNow, distanceFromScaleOriginAtStart)

    if (Math.abs(scale.x) > Math.abs(scale.y)) {
      scale.y = Math.abs(scale.x) * (scale.y < 0 ? -1 : 1)
    } else {
      scale.x = Math.abs(scale.y) * (scale.x < 0 ? -1 : 1)
    }

    scale = Vec2d.Clamp(scale, 0)

    newW = w * scale.x
    newH = h * scale.y
    const changeX = w - newW
    const changeY = h - newH

    if (this.info.handle === 'top_left') {
      maskWithPixelCoordinates.topLeft.x += -changeX
      maskWithPixelCoordinates.topLeft.y += -changeY
      maskWithPixelCoordinates.bottomRight.x += -changeX
      maskWithPixelCoordinates.bottomRight.y += -changeY
      if (maskWithPixelCoordinates.topLeft.x < 0 || maskWithPixelCoordinates.topLeft.y < 0) {
        return
      }
    } else if (this.info.handle === 'top_right') {
      maskWithPixelCoordinates.topLeft.y += -changeY
      maskWithPixelCoordinates.bottomRight.y += -changeY
      if (maskWithPixelCoordinates.topLeft.y < 0 || maskWithPixelCoordinates.bottomRight.x > newW) {
        return
      }
    } else if (this.info.handle === 'bottom_left') {
      maskWithPixelCoordinates.topLeft.x += -changeX
      maskWithPixelCoordinates.bottomRight.x += -changeX
      if (maskWithPixelCoordinates.topLeft.x < 0 || maskWithPixelCoordinates.bottomRight.y > newH) {
        return
      }
    } else if (this.info.handle === 'bottom_right') {
      if (maskWithPixelCoordinates.bottomRight.x > newW || maskWithPixelCoordinates.bottomRight.y > newH) {
        return
      }
    }

    newMask.topLeft.x = maskWithPixelCoordinates.topLeft.x / newW
    newMask.topLeft.y = maskWithPixelCoordinates.topLeft.y / newH
    newMask.bottomRight.x = maskWithPixelCoordinates.bottomRight.x / newW
    newMask.bottomRight.y = maskWithPixelCoordinates.bottomRight.y / newH

    const partial: TLShapePartial<TLBaseShape<string, { w: number; h: number; mask: ShapeMask }>> = {
      id: shape.id,
      type: shape.type,
      props: {
        mask: newMask,
      },
    }

    this.editor.updateShapes([partial], { squashing: true })
  }

  private complete() {
    if (this.info.onInteractionEnd) {
      this.editor.setCurrentTool(this.info.onInteractionEnd, this.info)
    } else {
      setMaskingShapeId(this.editor, null)
      this.parent.transition('idle')
    }
  }

  private cancel() {
    this.editor.bailToMark(this.markId)
    if (this.info.onInteractionEnd) {
      this.editor.setCurrentTool(this.info.onInteractionEnd, this.info)
    } else {
      setMaskingShapeId(this.editor, null)
      this.parent.transition('idle')
    }
  }

  private createSnapshot() {
    const selectionRotation = this.editor.getSelectionRotation()
    const {
      inputs: { originPagePoint },
    } = this.editor

    const shape = this.editor.getOnlySelectedShape() as MaskShape

    const selectionBounds = this.editor.getSelectionRotatedPageBounds()! // eslint-disable-line @typescript-eslint/no-non-null-assertion

    const dragHandlePoint = Vec2d.RotWith(
      selectionBounds.getHandlePoint(this.info.handle!), // eslint-disable-line @typescript-eslint/no-non-null-assertion
      selectionBounds.point,
      selectionRotation,
    )

    const cursorHandleOffset = Vec2d.Sub(originPagePoint, dragHandlePoint)

    return {
      shape,
      cursorHandleOffset,
    }
  }
}
